// SPDX-License-Identifier: Elastic-2.0
/**
 * Card — generic surface primitive.
 *
 * Polymorphic root via the `as` prop (defaults to `<div>`). Content-agnostic:
 * the caller decides what goes inside. Two visual variants and three padding
 * sizes; both styled exclusively with design-system tokens.
 *
 * Per T-010 § Instructions:
 *  - elevated → `--bg-elev-1` + `--shadow-2`
 *  - flat     → `--bg` + `--border-1` hairline
 *  - radius   → `--radius-4`
 *  - padding  → `--space-{4,5,6}` (sm | md | lg)
 *
 * No content-specific props — that's intentional. Composing components
 * (T-020 etc.) own header/body/footer layout.
 */
import type { ElementType, HTMLAttributes, ReactNode } from "react";

import styles from "./Card.module.css";

export type CardVariant = "elevated" | "flat";
export type CardPadding = "sm" | "md" | "lg";
export type CardElement = "div" | "section" | "article";

export interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Which HTML element to render. Defaults to `div`. */
  as?: CardElement;
  /** Visual surface treatment. Defaults to `elevated`. */
  variant?: CardVariant;
  /** Internal padding scale. Defaults to `md` (24px). */
  padding?: CardPadding;
  children?: ReactNode;
}

const PAD_CLASS: Record<CardPadding, string> = {
  sm: styles.padSm,
  md: styles.padMd,
  lg: styles.padLg,
};

const VARIANT_CLASS: Record<CardVariant, string> = {
  elevated: styles.elevated,
  flat: styles.flat,
};

export function Card({
  as = "div",
  variant = "elevated",
  padding = "md",
  className,
  children,
  ...rest
}: CardProps): JSX.Element {
  const Tag = as as ElementType;
  const composed = [styles.card, VARIANT_CLASS[variant], PAD_CLASS[padding], className]
    .filter(Boolean)
    .join(" ");
  return (
    <Tag className={composed} {...rest}>
      {children}
    </Tag>
  );
}
